import { useConnection } from "@solana/wallet-adapter-react";
import { useQuery } from "@tanstack/react-query";

import { coinSymbols } from "services/domain/coinSymbols";
import type { TokenValue, MarinadeState } from "services/domain/marinadeState";
import { fetchGlitchfulStakeApy } from "services/marinade/fetchGlitchfulStakeApy";
import { fetchLPInfo } from "services/marinade/fetchLPInfo";
import { fetchStakeInfos } from "services/marinade/fetchStakeInfos";
import { fetchTvlInfo } from "services/marinade/fetchTvlInfo";
import { getMarinadeState } from "services/marinade/sdk";

import { useIncentivesData } from "./useIncentivesData";
import { usePrices } from "./usePrices";

const convert = (sol: number, solUsd: number): TokenValue => {
  return { sol, usd: sol * solUsd };
};

const refetchInterval = 1 * 60 * 1000;
const staleTime = 1 * 60 * 1000;

export const useMarinadeState = (): MarinadeState | undefined => {
  const { connection } = useConnection();
  const prices = usePrices();

  const { incentivesAPY } = useIncentivesData();

  const solUSD = prices[coinSymbols.SOL]?.usd ?? 0;
  const mndeUSD = prices[coinSymbols.MNDE]?.usd ?? 0;

  const { data } = useQuery(
    [`marinade-state-${connection.rpcEndpoint}`],
    () => getMarinadeState(connection),
    {
      refetchInterval,
      staleTime,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const { data: stakeInfos } = useQuery(
    [`mSol-apy-${connection.rpcEndpoint}`],
    fetchStakeInfos,
    {
      refetchInterval,
      staleTime,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const { data: glitchfulApy } = useQuery(
    [`glitchfulApy-marinade`],
    () => fetchGlitchfulStakeApy(),
    {
      refetchInterval: 60 * 60 * 1000,
      staleTime: 60 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const { data: lpInfos } = useQuery(
    [`mSol-apy1d-${connection.rpcEndpoint}`],
    fetchLPInfo,
    {
      refetchInterval,
      staleTime,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const { data: tvlInfo } = useQuery([`tvl-Info`], fetchTvlInfo, {
    refetchInterval: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  return data && solUSD
    ? {
        sdkMarinadeState: data.sdkMarinadeState,
        tvlStaking: convert(data.activeStake, solUSD),
        userStake: convert(
          data.activeStake - (tvlInfo?.self_staked_sol ?? 0),
          solUSD
        ),
        selfStake: convert(tvlInfo?.self_staked_sol ?? 0, solUSD),
        mNativeStake: convert(tvlInfo?.marinade_native_stake_sol ?? 0, solUSD),
        reserveBalance: convert(data.reserveBalance, solUSD),
        totalTvl: convert(data.totalTvl, solUSD),
        mSolPrice: convert(data.mSolPrice, solUSD),
        solPrice: convert(1, solUSD),
        mndePrice: convert(1, mndeUSD),
        mSolSupply: (data.activeStake + data.stakeDelta) / data.mSolPrice,
        stakeDelta: data.stakeDelta,
        liquidityPool: {
          lpMint: data.liquidityPool.lpMint,
          solBalance: convert(data.liquidityPool.solBalance, solUSD),
          mSolBalance: convert(data.liquidityPool.mSolBalance, solUSD),
          tvl: convert(data.liquidityPool.tvl, solUSD),
          lpPrice: convert(data.liquidityPool.lpPrice, solUSD),
          cap: convert(data.liquidityPool.cap, solUSD),
          lpTarget: convert(data.liquidityPool.lpTarget, solUSD),
          supply: convert(data.liquidityPool.supply, solUSD),
          apy: {
            day: lpInfos?.lpApyDay ?? 0,
            week: lpInfos?.lpApyWeek ?? 0,
            year: lpInfos?.lpApy ?? 0,
          },
        },
        apy: stakeInfos?.mSolApy ?? {
          twoWeeks: 0,
          month: 0,
          quarter: 0,
          year: 0,
        },
        glitchfulApy: glitchfulApy ? glitchfulApy.apy * 100 : undefined,
        incentivesAPY,
        rentExemptForTokenAcc: data.rentExemptForTokenAcc,
        treasuryCut: data.treasuryCut,
        minStake: data.minStake,
      }
    : undefined;
};
